import { CatalogStatus, ServiceAttributeDefinition, ServiceAttributeFieldType, ServiceCategory, ServiceOffering } from '../types/Service';
import { normalizeCatalogAttachments } from '../../catalog/catalogAttachments';
import { apiRequest } from '../../../shared/services/apiClient';

export interface ServiceListQuery {
  q?: string;
  categoryId?: string;
  categoryIds?: string[];
  status?: CatalogStatus;
  sortBy?: 'name' | 'salePrice' | 'durationMinutes' | 'status' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  attributes?: Record<string, string>;
}

export interface CreateServiceRequest {
  name?: string;
  description?: string;
  categoryId?: string;
  code?: string;
  costPrice?: number;
  markupPercent?: number;
  salePrice?: number;
  taxPercent?: number;
  durationMinutes?: number;
  accountingCode?: string;
  status?: CatalogStatus;
  attributes?: Record<string, string | null>;
}

export interface UpdateServiceRequest extends CreateServiceRequest {
  clearCode?: boolean;
  clearCostPrice?: boolean;
  clearMarkupPercent?: boolean;
  clearSalePrice?: boolean;
  clearTaxPercent?: boolean;
  clearDurationMinutes?: boolean;
  clearAccountingCode?: boolean;
}

export interface CreateServiceCategoryRequest {
  name?: string;
  parentId?: string;
  defaultMarkupPercent?: number;
  sortOrder?: number;
}

export interface UpdateServiceCategoryRequest {
  name?: string;
  parentId?: string;
  clearParent?: boolean;
  defaultMarkupPercent?: number;
  clearDefaultMarkupPercent?: boolean;
  sortOrder?: number;
}

export interface CreateServiceAttributeRequest {
  name?: string;
  key?: string;
  fieldType?: ServiceAttributeFieldType;
  isRequired?: boolean;
  options?: string[];
  sortOrder?: number;
}

export interface UpdateServiceAttributeRequest {
  name?: string;
  key?: string;
  fieldType?: ServiceAttributeFieldType;
  isRequired?: boolean;
  options?: string[];
  clearOptions?: boolean;
  sortOrder?: number;
}

interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
}

const statuses: CatalogStatus[] = ['active', 'archived'];
const fieldTypes: ServiceAttributeFieldType[] = ['text', 'number', 'date', 'select', 'yesNo'];

const normalizeFieldType = (value: unknown): ServiceAttributeFieldType => {
  const normalized = String(value ?? '').trim();
  const camel = normalized.charAt(0).toLowerCase() + normalized.slice(1);
  return fieldTypes.includes(camel as ServiceAttributeFieldType) ? camel as ServiceAttributeFieldType : 'text';
};

const normalizeAttributes = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, String(item ?? '').trim()] as const)
      .filter(([, item]) => item.length > 0),
  );
};

const normalizeAttribute = (value: any): ServiceAttributeDefinition => ({
  id: String(value?.id ?? ''),
  categoryId: String(value?.categoryId ?? ''),
  name: String(value?.name ?? ''),
  key: String(value?.key ?? ''),
  fieldType: normalizeFieldType(value?.fieldType),
  isRequired: Boolean(value?.isRequired),
  options: Array.isArray(value?.options) ? value.options.map((item: unknown) => String(item ?? '').trim()).filter(Boolean) : [],
  sortOrder: optionalNumber(value?.sortOrder) ?? 0,
  inherited: Boolean(value?.inherited),
  sourceCategoryName: optionalText(value?.sourceCategoryName),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeStatus = (value: unknown): CatalogStatus => {
  const normalized = String(value ?? '').toLowerCase();
  return statuses.includes(normalized as CatalogStatus) ? normalized as CatalogStatus : 'active';
};

const optionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const optionalText = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  return text || undefined;
};

const normalizeService = (value: any): ServiceOffering => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  description: String(value?.description ?? ''),
  categoryId: String(value?.categoryId ?? ''),
  categoryName: String(value?.categoryName ?? ''),
  code: optionalText(value?.code),
  costPrice: optionalNumber(value?.costPrice),
  markupPercent: optionalNumber(value?.markupPercent),
  salePrice: optionalNumber(value?.salePrice),
  taxPercent: optionalNumber(value?.taxPercent),
  durationMinutes: optionalNumber(value?.durationMinutes),
  accountingCode: optionalText(value?.accountingCode),
  imageUrl: optionalText(value?.imageUrl),
  attachments: normalizeCatalogAttachments(value?.attachments),
  attributes: normalizeAttributes(value?.attributes),
  status: normalizeStatus(value?.status),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeCategory = (value: any): ServiceCategory => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  parentId: optionalText(value?.parentId),
  defaultMarkupPercent: optionalNumber(value?.defaultMarkupPercent),
  sortOrder: optionalNumber(value?.sortOrder) ?? 0,
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && !payload.id) {
    return payload.data;
  }
  return payload;
};

const buildQuery = (path: string, query?: Record<string, string | number | string[] | undefined>): string => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item) params.append(key, item);
      });
      return;
    }
    params.set(key, String(value));
  });
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const withAttributes = (path: string, attributes?: Record<string, string>) => {
  if (!attributes) return path;
  const params = new URLSearchParams();
  Object.entries(attributes).forEach(([id, value]) => {
    if (!value.trim()) return;
    params.set(`attributes[${id}]`, value.trim());
  });
  const extra = params.toString();
  if (!extra) return path;
  return path.includes('?') ? `${path}&${extra}` : `${path}?${extra}`;
};

const notFoundToNull = async <T>(work: () => Promise<T>): Promise<T | null> => {
  try {
    return await work();
  } catch (error) {
    if (error instanceof Error && /404|not found/i.test(error.message)) {
      return null;
    }
    throw error;
  }
};

export const serviceCatalogService = {
  getServices: async (query?: ServiceListQuery): Promise<ServiceOffering[]> => {
    const payload = await apiRequest<PagedResult<any> | any[]>(withAttributes(buildQuery('/api/v1/services', {
      q: query?.q,
      categoryId: query?.categoryIds?.length ? undefined : query?.categoryId,
      categoryIds: query?.categoryIds?.length ? query.categoryIds : undefined,
      status: query?.status,
      sortBy: query?.sortBy,
      sortDir: query?.sortDir,
      page: query?.page,
      pageSize: query?.pageSize,
    }), query?.attributes));
    return pickItems(payload).map(normalizeService);
  },

  getServiceById: async (id: string): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}`));
      return item ? normalizeService(item) : null;
    }),

  createService: async (request: CreateServiceRequest): Promise<ServiceOffering | null> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/services', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeService(item) : null;
  },

  updateService: async (id: string, request: UpdateServiceRequest): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeService(item) : null;
    }),

  archiveService: async (id: string): Promise<void> => {
    await apiRequest(`/api/v1/services/${id}`, { method: 'DELETE' });
  },

  updateServiceStatus: async (id: string, status: CatalogStatus): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }));
      return item ? normalizeService(item) : null;
    }),

  uploadServiceAttachment: async (id: string, file: File, setAsPrimary = false): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('setAsPrimary', setAsPrimary ? 'true' : 'false');
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}/attachments`, {
        method: 'POST',
        body: formData,
      }));
      return item ? normalizeService(item) : null;
    }),

  removeServiceAttachment: async (id: string, attachmentId: string): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}/attachments/${attachmentId}`, {
        method: 'DELETE',
      }));
      return item ? normalizeService(item) : null;
    }),

  setServicePrimaryImage: async (id: string, attachmentId: string): Promise<ServiceOffering | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/services/${id}/attachments/${attachmentId}/primary`, {
        method: 'POST',
      }));
      return item ? normalizeService(item) : null;
    }),

  getCategories: async (query?: { q?: string; parentId?: string }): Promise<ServiceCategory[]> => {
    const payload = await apiRequest<any>(buildQuery('/api/v1/service-categories', {
      q: query?.q,
      parentId: query?.parentId,
    }));
    return pickItems(payload).map(normalizeCategory);
  },

  createCategory: async (request: CreateServiceCategoryRequest): Promise<ServiceCategory | null> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/service-categories', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeCategory(item) : null;
  },

  updateCategory: async (id: string, request: UpdateServiceCategoryRequest): Promise<ServiceCategory | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/service-categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeCategory(item) : null;
    }),

  deleteCategory: async (id: string): Promise<void> => {
    await apiRequest(`/api/v1/service-categories/${id}`, { method: 'DELETE' });
  },

  getAttributes: async (categoryId: string, includeInherited = false): Promise<ServiceAttributeDefinition[]> => {
    const payload = await apiRequest<any>(buildQuery(`/api/v1/service-categories/${categoryId}/attributes`, {
      includeInherited: includeInherited ? 'true' : undefined,
    }));
    return pickItems(payload).map(normalizeAttribute);
  },

  createAttribute: async (categoryId: string, request: CreateServiceAttributeRequest): Promise<ServiceAttributeDefinition | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/service-categories/${categoryId}/attributes`, {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeAttribute(item) : null;
  },

  updateAttribute: async (categoryId: string, attributeId: string, request: UpdateServiceAttributeRequest): Promise<ServiceAttributeDefinition | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/service-categories/${categoryId}/attributes/${attributeId}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeAttribute(item) : null;
    }),

  deleteAttribute: async (categoryId: string, attributeId: string): Promise<void> => {
    await apiRequest(`/api/v1/service-categories/${categoryId}/attributes/${attributeId}`, { method: 'DELETE' });
  },
};

export const getServices = serviceCatalogService.getServices;
export const getServiceById = serviceCatalogService.getServiceById;
export const createService = serviceCatalogService.createService;
export const updateService = serviceCatalogService.updateService;
export const archiveService = serviceCatalogService.archiveService;
export const updateServiceStatus = serviceCatalogService.updateServiceStatus;
export const uploadServiceAttachment = serviceCatalogService.uploadServiceAttachment;
export const removeServiceAttachment = serviceCatalogService.removeServiceAttachment;
export const setServicePrimaryImage = serviceCatalogService.setServicePrimaryImage;
export const getServiceCategories = serviceCatalogService.getCategories;
